import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/axios';
import CModal from '../shared/c-modal';
import { DayAmendments, DayTimeline, DayTotals } from './daySections';
import { COMPANY_TIME_ZONE } from '../../constants';

const PAGE_SIZE = 20;

// Only a fallback: the Day response carries its own `timezone`, so use that when present.
export const FALLBACK_TZ = COMPANY_TIME_ZONE;

const SAVE_ERROR = {
  SELF_ACKNOWLEDGE: 'You cannot acknowledge your own request - ask another manager or HR.',
  ALREADY_ACKNOWLEDGED: 'This amendment was already acknowledged.',
  NOT_PENDING: 'This request was already decided.',
};

// These mean the row moved on without us, so the queue has to reload.
const STALE_CODES = ['ALREADY_ACKNOWLEDGED', 'NOT_PENDING'];

export const actionButtonClass = 'rounded border px-2 py-1 text-xs font-medium';

export const employeeColumn = {
  key: 'employee',
  header: 'Employee',
  cellClassName: 'font-medium',
  render: (row) => row.employee_name || `Employee #${row.employee_id}`,
};

export const dayColumn = { key: 'day', header: 'Day', cellClassName: 'text-gray-500' };

/**
 * One employee's day as the mobile app computes it: the punch timeline with its
 * source tags, the effective totals, and every amendment on that day.
 */
const DayModal = ({ row, onClose }) => {
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/api/timesheet/days/${row.day}`, { params: { employee_id: row.employee_id } })
      .then((res) => { if (alive) setDay(res.data.data); })
      .catch(() => toast.error('Failed to load day'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [row.day, row.employee_id]);

  const zone = (day && day.timezone) || FALLBACK_TZ;

  return (
    <CModal
      onClose={onClose}
      labelledBy="day-title"
      overlayClassName="bg-black/40"
      className="w-full max-w-lg rounded-lg bg-white shadow-xl"
      data-testid="day-modal"
    >
      <div className="flex items-start justify-between border-b border-gray-200 p-4">
        <div>
          <h2 id="day-title" className="text-base font-semibold text-gray-900">
            {row.employee_name || `Employee #${row.employee_id}`}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{row.day}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-4 text-sm">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}
        {day && (
          <>
            <DayTotals day={day} timeZone={zone} />
            <DayTimeline events={day.events} timeZone={zone} />
            <DayAmendments amendments={day.amendments} timeZone={zone} />
          </>
        )}
      </div>
    </CModal>
  );
};

/**
 * What an approvals queue holds on its own: the server page it shows, the inline
 * confirmation and the day-detail modal. Filter values come from the shared row
 * above the sections; each queue maps them onto its endpoint's params.
 */
export const useApprovalQueue = ({ endpoint, failureMessage, filters, buildParams }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reloads, setReloads] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [acting, setActing] = useState(false);
  const [viewing, setViewing] = useState(null);
  // A page number belongs to the filter values it was picked under, so a filter
  // change lands back on page 1 without a fetch for the page we just left.
  const [paging, setPaging] = useState({ filtersKey: '', page: 1 });

  const filterParams = buildParams(filters);
  const filtersKey = JSON.stringify(filterParams);
  const page = paging.filtersKey === filtersKey ? paging.page : 1;
  const setPage = (next) => setPaging({ filtersKey, page: next });
  const params = { ...filterParams, page, limit: PAGE_SIZE };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(endpoint, { params })
      .then((res) => {
        if (!alive) return;
        setRows(res.data.data || []);
        setTotal(res.data.total ?? 0);
      })
      .catch(() => toast.error(failureMessage))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, filtersKey, page, reloads]);

  const reload = () => setReloads((count) => count + 1);

  const save = async (request, successMessage) => {
    setActing(true);
    try {
      await request();
      toast.success(successMessage);
      setConfirm(null);
      reload();
    } catch (err) {
      const code = err.response?.data?.code;
      toast.error(SAVE_ERROR[code] || err.response?.data?.message || 'Failed to save');
      if (STALE_CODES.includes(code)) {
        setConfirm(null);
        reload();
      }
    } finally {
      setActing(false);
    }
  };

  return {
    rows,
    total,
    totalPages: Math.ceil(total / PAGE_SIZE),
    loading,
    page,
    setPage,
    confirm,
    setConfirm,
    acting,
    save,
    viewing,
    setViewing,
  };
};

/**
 * The right-hand cell of a queue row: View day plus that queue's own decision
 * buttons, replaced in place by a confirmation once a decision is picked.
 * A queue that needs something answered before the decision lands passes it as
 * `fields`, and holds Confirm shut with `confirmDisabled` until it is answered.
 */
export const RowActions = ({
  queue, prefix, row, prompt, fields, confirmDisabled, onConfirm, children,
}) => {
  if (queue.confirm?.id === row.id) {
    return (
      <div className="inline-block text-left" data-testid={`${prefix}-${row.id}-confirm`}>
        <p className="text-xs text-gray-600">{prompt}</p>
        {fields}
        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => queue.setConfirm(null)}
            className={`${actionButtonClass} border-gray-300 text-gray-700 hover:bg-gray-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={queue.acting || confirmDisabled}
            data-testid={`${prefix}-${row.id}-confirm-yes`}
            className={`${actionButtonClass} border-primary-600 bg-primary-600 text-white`
              + ' hover:bg-primary-700 disabled:opacity-60'}
          >
            {queue.acting ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={() => queue.setViewing(row)}
        data-testid={`${prefix}-${row.id}-view`}
        className={`${actionButtonClass} border-gray-300 text-gray-700 hover:bg-gray-50`}
      >
        View day
      </button>
      {children}
    </div>
  );
};

/**
 * A titled block on the Approvals tab. Both queues sit on one scroll, so each
 * carries its own heading, count and day-detail modal.
 */
export const QueueSection = ({ queue, testId, title, count, description, children }) => (
  <section className="space-y-4" data-testid={`${testId}-section`}>
    <div>
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <span
          data-testid={`${testId}-count`}
          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
        >
          {count}
        </span>
      </div>
      <p className="mt-1 text-sm text-gray-600">{description}</p>
    </div>
    {children}
    {queue.viewing && <DayModal row={queue.viewing} onClose={() => queue.setViewing(null)} />}
  </section>
);
